import {useCallback, useState} from 'react'
import {type AppBskyFeedDefs, AtUri} from '@atproto/api'
import {useNavigation} from '@react-navigation/native'

import {type NavigationProp} from '#/lib/routes/types'
import {isWeb} from '#/platform/detection'
import {type Trend} from './data/trends'

/**
 * Returns a handler that drops a trend into the `/search` query.
 *
 * Used by every trend card's primary action (Hero / Featured "Explore trend"
 * buttons; Compact / Carousel whole-card press). Because every trend card
 * currently renders inside `WSearchScreen`, this is implemented as
 * `setParams({q: trend.title})` — a same-route param update, not a push.
 * If we ever surface trend cards from another screen (a profile module, a
 * compose pre-fill, etc.) we'll need a `navigateToTrendSearch` variant that
 * pushes the Search route instead.
 */
export function useTrendSearchPress() {
  const navigation = useNavigation<NavigationProp>()
  return useCallback(
    (trend: Trend) => {
      navigation.setParams({q: trend.title})
      // Same-route param update doesn't reset scroll, so the page would stay
      // mid-feed. Jump back to the top so the trend's results start at the top.
      if (isWeb && typeof window !== 'undefined') {
        window.scrollTo(0, 0)
      }
    },
    [navigation],
  )
}

/**
 * Display rule for the active-voices treatment on trend cards
 * (avatar stack + "active voices" label):
 *
 * - fewer than 2 voices → hide the treatment entirely (avatars included)
 * - 2–5 voices          → avatars + label, **no count**
 * - more than 5 voices  → avatars + count + label
 *
 * Shared by every card that renders voices (Hero / Compact / Featured /
 * Carousel cards and `TrendSearchHeader`) so the thresholds live in one place.
 */
export function getVoicesDisplay(count: number | undefined): {
  /** Render the voices treatment at all (avatars + label). */
  showVoices: boolean
  /** Include the formatted count before the label. */
  showCount: boolean
} {
  const n = count ?? 0
  return {showVoices: n >= 2, showCount: n > 5}
}

/**
 * Hashtag for a trend: the backend's first `query.hashtags` entry when present,
 * otherwise the title CamelCased ("EU AI Act" → "#EUAIAct"). Always returned
 * with a leading "#". Shared by the trend composer prompt and the article
 * "Create post" action so both prefill the same tag.
 */
export function trendHashtag(trend: Trend): string {
  const fromQuery = trend.query?.hashtags?.[0]
  if (fromQuery) {
    return fromQuery.startsWith('#') ? fromQuery : `#${fromQuery}`
  }
  const camel = trend.title
    .split(/\s+/)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join('')
    // Keep letters (incl. å/ä/ö etc.), digits and underscore — strip the rest.
    .replace(/[^\p{L}\p{N}_]/gu, '')
  return `#${camel}`
}

/**
 * Returns a handler that opens a post's thread screen.
 *
 * Shared by the inline post previews rendered inside trend cards
 * (`CompactPostPreview`, `FullPostPreview`).
 */
export function usePostThreadPress() {
  const navigation = useNavigation<NavigationProp>()
  return useCallback(
    (post: AppBskyFeedDefs.PostView) => {
      navigation.navigate('PostThread', {
        name: post.author.did,
        rkey: new AtUri(post.uri).rkey,
      })
    },
    [navigation],
  )
}

/**
 * Tracks whether an image URL failed to load, so a card can fall through to
 * its placeholder instead of showing a blank area.
 *
 * Trend and article images are served straight from the publisher (no CDN of
 * ours in between), so a site that is blocked or unreachable for this viewer
 * takes its images down with it - while the data still says "image present".
 * Presence in the data is therefore not enough; the load result decides.
 *
 * Keyed by URL, so a data refresh that swaps the image retries automatically.
 */
export function useImageLoadFailed(url: string | undefined): {
  failed: boolean
  onError: () => void
} {
  const [failedUrl, setFailedUrl] = useState<string | null>(null)
  return {
    failed: !!url && failedUrl === url,
    onError: () => {
      if (url) setFailedUrl(url)
    },
  }
}
